import React from 'react';
import { cn } from '@/lib/utils';

export type StatusType =
  | 'HEALTHY'
  | 'WARNING'
  | 'STALE'
  | 'HALTED'
  | 'ERROR'
  | 'PAPER_MODE'
  | 'LIVE'
  | 'OFFLINE'
  | 'OPEN'
  | 'CLOSED'
  | 'PENDING'
  | 'COMPLETED'
  | 'REJECTED'
  | 'ACCEPTED'
  | 'RUNNING'
  | 'IDLE'
  | 'MARKET_OPEN'
  | 'MARKET_CLOSED';

interface StatusBadgeProps {
  status: string | StatusType | null | undefined;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  showDot?: boolean;
  className?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  label,
  size = 'md',
  showDot = true,
  className,
}) => {
  const normalizedStatus = (status || 'UNKNOWN').toString().toUpperCase();
  const displayLabel = label || normalizedStatus.replace(/_/g, ' ');

  const getStyle = (s: string) => {
    switch (s) {
      case 'POSITION_OPENED':
      case 'HEALTHY':
      case 'LIVE':
      case 'RUNNING':
      case 'MARKET_OPEN':
      case 'ACCEPTED':
      case 'COMPLETED':
      case 'OPEN':
      case 'SUCCESS':
        return {
          container: 'bg-emerald-950/60 text-emerald-400 border-emerald-800/50',
          dot: 'bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.8)]',
        };

      case 'BLOCKED_RISK':
      case 'RISK_BLOCKED':
      case 'WARNING':
      case 'STALE':
      case 'PENDING CYCLE':
        return {
          container: 'bg-amber-950/60 text-amber-400 border-amber-800/50',
          dot: 'bg-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.8)]',
        };

      case 'RECEIVED':
      case 'PENDING':
      case 'WAITING':
        return {
          container: 'bg-blue-950/60 text-blue-400 border-blue-800/50',
          dot: 'bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.8)]',
        };

      case 'EXECUTION_UNKNOWN':
      case 'UNKNOWN_EXECUTION':
        return {
          container: 'bg-purple-950/60 text-purple-300 border-purple-800/50',
          dot: 'bg-purple-400 shadow-[0_0_8px_rgba(192,132,252,0.8)]',
        };

      case 'ORDER_FAILED':
      case 'FAILED':
        return {
          container: 'bg-red-950/60 text-red-400 border-red-800/50',
          dot: 'bg-red-400 shadow-[0_0_8px_rgba(239,68,68,0.8)]',
        };

      case 'ORDER_REJECTED':
      case 'REJECTED':
      case 'HALTED':
      case 'ERROR':
      case 'CANCELLED':
      case 'CRITICAL':
        return {
          container: 'bg-rose-950/60 text-rose-400 border-rose-800/50',
          dot: 'bg-rose-400 shadow-[0_0_8px_rgba(244,63,94,0.8)]',
        };

      case 'PAPER_MODE':
      case 'PAPER MODE':
        return {
          container: 'bg-cyan-950/60 text-cyan-300 border-cyan-700/60 shadow-[0_0_10px_rgba(6,182,212,0.15)] font-semibold',
          dot: 'bg-cyan-400 shadow-[0_0_8px_rgba(6,182,212,0.9)]',
        };

      case 'PROCESSED':
      case 'OFFLINE':
      case 'CLOSED':
      case 'IDLE':
      case 'MARKET_CLOSED':
      default:
        return {
          container: 'bg-slate-800/60 text-slate-300 border-slate-700/50',
          dot: 'bg-slate-400',
        };
    }
  };

  const style = getStyle(normalizedStatus);

  const sizeClasses = {
    sm: 'text-[10px] px-1.5 py-0.5 gap-1',
    md: 'text-xs px-2.5 py-0.5 gap-1.5',
    lg: 'text-sm px-3 py-1 gap-2',
  };

  const dotSizes = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-mono border font-medium uppercase tracking-wider transition-all duration-200 ease-out select-none',
        sizeClasses[size],
        style.container,
        className
      )}
    >
      {showDot && (
        <span
          className={cn('rounded-full shrink-0 transition-colors duration-200', dotSizes[size], style.dot)}
        />
      )}
      <span>{displayLabel}</span>
    </span>
  );
};
